/** MUI loads this lazily for image export; import so Vite always bundles it. */
import 'rasterizehtml';

import { getIsPlainRecord } from 'src/utils/get-is-plain-record';

import { getIsMedianSuppressed } from './insight-chart-series';
import type { InsightChartModel } from './insight-chart-series';

/** File name for an image export, without the extension MUI appends. */
export function getInsightChartExportFileName(title: string): string {
  const cleaned = title
    .trim()
    .replaceAll(/[<>:"/\\|?*]/g, '')
    .replaceAll(/\s+/g, ' ')
    .trim();
  return cleaned || 'chart';
}

export function getCanExportInsightChart(model: InsightChartModel | null): boolean {
  return Boolean(model && model.xLabels.length > 0 && !getIsMedianSuppressed(model));
}

export type InsightChartImageExportApi = {
  exportAsImage: (options?: {
    fileName?: string;
    onBeforeExport?: (iframe: HTMLIFrameElement) => Promise<void> | void;
    type?: string;
  }) => void;
};

export function getHasChartImageExport(api: unknown): api is InsightChartImageExportApi {
  if (!getIsPlainRecord(api)) return false;
  return typeof api.exportAsImage === 'function';
}

/**
 * Swap MUI's plot-only clone for the Chart card body: title, description, plot,
 * legend, and overflow summary, minus chrome marked `data-hide-on-export`.
 * The bordered Paper stays out of the clone so its 1px outline is not cropped
 * at the canvas edge.
 */
export function fillInsightChartExportDocument(doc: Document, source: HTMLElement): boolean {
  const clone = source.cloneNode(true);
  if (!getIsHTMLElement(clone)) return false;
  clone.querySelectorAll('[data-hide-on-export]').forEach(node => node.remove());
  const width = source.getBoundingClientRect().width;
  clone.style.backgroundColor = '#fff';
  clone.style.border = 'none';
  clone.style.boxSizing = 'border-box';
  clone.style.overflow = 'visible';
  if (width > 0) clone.style.width = `${width}px`;
  doc.body.replaceChildren(clone);
  return true;
}

export function fillInsightChartExportIframe(iframe: HTMLIFrameElement, source: HTMLElement): void {
  const doc = iframe.contentDocument;
  if (!doc) return;
  fillInsightChartExportDocument(doc, source);
}

function getIsHTMLElement(value: Node): value is HTMLElement {
  return value instanceof HTMLElement;
}
